/**
 * LiveKit Voice Session Service
 *
 * This service provides integration with LiveKit for real-time voice communication
 * during training sessions. It handles room connections, audio controls, and
 * speaker state management using the actual LiveKit SDK.
 *
 * @module services/livekit
 */

import { Room, RoomEvent, Track, Participant, RemoteTrack, RemoteTrackPublication } from 'livekit-client';
import { getIdToken } from './auth';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

/**
 * Connection state for the LiveKit room.
 */
export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed';

/**
 * Speaker state indicating who is currently speaking.
 */
export type SpeakerState = 'user' | 'ai' | 'none';

/**
 * Room information returned after successful connection.
 */
export interface RoomInfo {
  name: string;
  participantCount: number;
  isMuted: boolean;
  connectionState: ConnectionState;
}

/**
 * Session creation response from the API.
 */
export interface SessionResponse {
  roomName: string;
  token: string;
  serverUrl: string;
  scenario: string;
  role: string;
  sessionId: string;  // Session ID for evaluation lookup
}

/**
 * Scenario information.
 */
export interface ScenarioInfo {
  id: string;
  name: string;
  role: string;
  industry: string;
  description: string;
}

export type SpeakerChangeCallback = (speaker: SpeakerState) => void;
export type ConnectionStateCallback = (state: ConnectionState) => void;
export type ErrorCallback = (error: LiveKitError) => void;

export type LiveKitErrorCode =
  | 'ConnectionFailed'
  | 'InvalidToken'
  | 'RoomNotFound'
  | 'MediaDeviceError'
  | 'NetworkError'
  | 'ApiError'
  | 'UnknownError';

export class LiveKitError extends Error {
  code: LiveKitErrorCode;
  originalError?: Error;

  constructor(message: string, code: LiveKitErrorCode, originalError?: Error) {
    super(message);
    this.name = 'LiveKitError';
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * LiveKit Service Implementation using actual LiveKit SDK
 */
class LiveKitService {
  private room: Room | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private muted: boolean = false;
  private currentSpeaker: SpeakerState = 'none';
  private speakerChangeCallbacks: Set<SpeakerChangeCallback> = new Set();
  private connectionStateCallbacks: Set<ConnectionStateCallback> = new Set();
  private errorCallbacks: Set<ErrorCallback> = new Set();

  /**
   * Fetch available scenarios from the API.
   */
  async getScenarios(): Promise<ScenarioInfo[]> {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/livekit/scenarios`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new LiveKitError('Failed to fetch scenarios', 'ApiError');
      }

      const data = await response.json();
      return data.scenarios;
    } catch (error) {
      // Fallback to hardcoded scenarios if API fails
      console.warn('Failed to fetch scenarios from API, using fallback:', error);
      return this.getFallbackScenarios();
    }
  }

  /**
   * Fallback scenarios when API is not available.
   */
  private getFallbackScenarios(): ScenarioInfo[] {
    return [
      {
        id: 'ShopSmart',
        name: 'ShopSmart Retail Operations',
        role: 'Chief Operating Officer',
        industry: 'E-commerce',
        description: 'Practice discovery with a retail COO facing margin erosion and supply chain challenges'
      },
      {
        id: 'Acme',
        name: 'Acme Risk Management',
        role: 'Chief Risk Officer',
        industry: 'Financial Services',
        description: 'Engage with a CRO on risk management and compliance challenges'
      },
      {
        id: 'ABC_CPG_Cost_Optimization',
        name: 'ABC CPG Cost Optimization',
        role: 'VP of Digital Manufacturing',
        industry: 'Manufacturing',
        description: 'Discuss cost optimization strategies with a VP of Digital Manufacturing'
      },
      {
        id: 'Apex_Semiconductor',
        name: 'Apex Semiconductor',
        role: 'CIO',
        industry: 'Technology',
        description: 'Work with a semiconductor CIO on technology modernization'
      },
      {
        id: 'Christies_Auction',
        name: "Christie's Auction House",
        role: 'CIO',
        industry: 'Financial Services',
        description: 'Engage with an auction house CIO on digital transformation'
      },
      {
        id: 'EngageMe_Digital',
        name: 'EngageMe Digital Marketing',
        role: 'CEO',
        industry: 'Technology',
        description: 'Discuss growth strategies with a digital marketing CEO'
      },
      {
        id: 'InsureRight_Corporation',
        name: 'InsureRight Corporation',
        role: 'COO',
        industry: 'Financial Services',
        description: 'Work with an insurance COO on operational efficiency'
      },
      {
        id: 'LMN_Tech_Architecture',
        name: 'LMN Tech Architecture',
        role: 'CEO',
        industry: 'Technology',
        description: 'Discuss architecture modernization with a tech CEO'
      },
      {
        id: 'SecureWealth_Financial',
        name: 'SecureWealth Financial',
        role: 'CDO',
        industry: 'Financial Services',
        description: 'Engage with a financial services CDO on data strategy'
      },
      {
        id: 'TechStyle_Retail',
        name: 'TechStyle Retail',
        role: 'CIO',
        industry: 'E-commerce',
        description: 'Work with a retail CIO on technology transformation'
      },
      {
        id: 'StateU_Digital_Learning',
        name: 'StateU Digital Learning',
        role: 'Chief Information Officer',
        industry: 'Education',
        description: 'Engage with a university CIO on digital learning transformation and enrollment growth'
      },
      {
        id: 'Metro_City_Government',
        name: 'Metro City Digital Government',
        role: 'Chief Technology Officer',
        industry: 'Public Sector',
        description: 'Work with a city CTO on modernizing citizen services and digital government'
      },
      {
        id: 'HealthFirst_Medical',
        name: 'HealthFirst Regional Medical Center',
        role: 'Chief Information Officer',
        industry: 'Healthcare and Life Sciences',
        description: 'Discuss clinical operations and digital health with a healthcare CIO'
      },
      {
        id: 'GlobalReach_Relief',
        name: 'GlobalReach International',
        role: 'Chief Operating Officer',
        industry: 'Non-Profit',
        description: 'Engage with an international relief organization COO on impact transparency'
      },
      {
        id: 'PharmaGenix_Research',
        name: 'PharmaGenix Biopharmaceutical',
        role: 'Chief Scientific Officer',
        industry: 'Healthcare and Life Sciences',
        description: 'Work with a biopharma CSO on accelerating drug discovery with AI'
      },
      {
        id: 'TechPrep_Academy',
        name: 'TechPrep Academy Network',
        role: 'Chief Academic Officer',
        industry: 'Education',
        description: 'Discuss workforce development and student outcomes with an education CAO'
      },
      {
        id: 'VitalCare_Health',
        name: 'VitalCare Global Health Foundation',
        role: 'Chief Operating Officer',
        industry: 'Non-Profit',
        description: 'Engage with a global health foundation COO on patient outcome tracking'
      },
      {
        id: 'BrightPath_Education',
        name: 'BrightPath Global Education Alliance',
        role: 'Chief Executive Officer',
        industry: 'Non-Profit',
        description: 'Work with an education NGO CEO on proving learning outcomes at scale'
      },
      {
        id: 'GreenHarvest_Agriculture',
        name: 'GreenHarvest Alliance for Food Security',
        role: 'Chief Executive Officer',
        industry: 'Non-Profit',
        description: 'Discuss food security and farmer outcomes with an agriculture NGO CEO'
      }
    ];
  }

  /**
   * Create a session via the API and get connection details.
   */
  async createSession(scenarioId: string, scenarioName: string, role: string): Promise<SessionResponse> {
    // Dev mode: return mock session data to test UI without real backend
    if (!process.env.REACT_APP_USER_POOL_ID) {
      console.log('[Dev Mode] Returning mock session response');
      return {
        serverUrl: 'wss://localhost:7880',
        token: 'dev-mock-token',
        sessionId: `dev-session-${scenarioId}-${Date.now()}`,
        roomName: `dev-room-${scenarioId}`,
        scenario: scenarioId,
        role: role,
      };
    }

    const token = await getIdToken();
    
    const response = await fetch(`${API_BASE_URL}/livekit/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        scenarioId,
        scenarioName,
        role,
        participantName: `user-${Date.now()}`
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new LiveKitError(`Failed to create session: ${error}`, 'ApiError');
    }

    return response.json();
  }

  /**
   * Connect to a LiveKit room.
   */
  async connect(roomUrl: string, token: string): Promise<RoomInfo> {
    // Dev mode: simulate a successful connection without real WebSocket
    if (token === 'dev-mock-token') {
      console.log('[Dev Mode] Simulating LiveKit connection');
      this.setConnectionState('connected');
      return {
        name: 'dev-room',
        participantCount: 2,
        isMuted: false,
        connectionState: 'connected',
      };
    }

    if (!roomUrl || !token) {
      throw new LiveKitError('Room URL and token are required', 'InvalidToken');
    }

    this.setConnectionState('connecting');

    try {
      // Create new room instance
      this.room = new Room();

      // Set up event handlers
      this.setupRoomEventHandlers();

      // Connect to the room
      await this.room.connect(roomUrl, token);

      // Enable microphone
      await this.room.localParticipant.setMicrophoneEnabled(true);

      this.setConnectionState('connected');

      return {
        name: this.room.name,
        participantCount: this.room.remoteParticipants.size + 1,
        isMuted: this.muted,
        connectionState: 'connected',
      };
    } catch (error) {
      this.setConnectionState('failed');
      const liveKitError = new LiveKitError(
        'Failed to connect to room',
        'ConnectionFailed',
        error instanceof Error ? error : undefined
      );
      this.notifyError(liveKitError);
      throw liveKitError;
    }
  }

  /**
   * Set up LiveKit room event handlers.
   */
  private setupRoomEventHandlers(): void {
    if (!this.room) return;

    this.room.on(RoomEvent.Disconnected, () => {
      this.setConnectionState('disconnected');
      this.setSpeaker('none');
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      this.setConnectionState('reconnecting');
    });

    this.room.on(RoomEvent.Reconnected, () => {
      this.setConnectionState('connected');
    });

    this.room.on(RoomEvent.ParticipantConnected, (participant: Participant) => {
      console.log('Participant connected:', participant.identity);
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant: Participant) => {
      console.log('Participant disconnected:', participant.identity);
    });

    // Track speaking state based on audio levels
    this.room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
      if (speakers.length === 0) {
        this.setSpeaker('none');
      } else {
        // Check if local participant is speaking
        const localSpeaking = speakers.some(s => s === this.room?.localParticipant);
        if (localSpeaking) {
          this.setSpeaker('user');
        } else {
          // Remote participant (AI agent) is speaking
          this.setSpeaker('ai');
        }
      }
    });

    this.room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, publication: RemoteTrackPublication, participant: Participant) => {
      if (track.kind === Track.Kind.Audio) {
        // Attach audio track to play it
        const audioElement = document.createElement('audio');
        audioElement.autoplay = true;
        track.attach(audioElement);
        document.body.appendChild(audioElement);
      }
    });
  }

  /**
   * Disconnect from the current room.
   */
  disconnect(): void {
    if (this.room) {
      this.room.disconnect();
      this.room = null;
    }
    this.currentSpeaker = 'none';
    this.muted = false;
    this.setConnectionState('disconnected');
    this.notifySpeakerChange('none');
  }

  /**
   * Toggle the microphone mute state.
   */
  toggleMute(): boolean {
    this.muted = !this.muted;
    
    if (this.room) {
      this.room.localParticipant.setMicrophoneEnabled(!this.muted);
    }

    if (this.muted && this.currentSpeaker === 'user') {
      this.setSpeaker('none');
    }

    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  getCurrentSpeaker(): SpeakerState {
    return this.currentSpeaker;
  }

  onSpeakerChange(callback: SpeakerChangeCallback): () => void {
    this.speakerChangeCallbacks.add(callback);
    return () => this.speakerChangeCallbacks.delete(callback);
  }

  onConnectionStateChange(callback: ConnectionStateCallback): () => void {
    this.connectionStateCallbacks.add(callback);
    return () => this.connectionStateCallbacks.delete(callback);
  }

  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.add(callback);
    return () => this.errorCallbacks.delete(callback);
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.connectionStateCallbacks.forEach((callback) => {
      try {
        callback(state);
      } catch (error) {
        console.error('Error in connection state callback:', error);
      }
    });
  }

  private setSpeaker(speaker: SpeakerState): void {
    if (this.currentSpeaker !== speaker) {
      this.currentSpeaker = speaker;
      this.notifySpeakerChange(speaker);
    }
  }

  private notifySpeakerChange(speaker: SpeakerState): void {
    this.speakerChangeCallbacks.forEach((callback) => {
      try {
        callback(speaker);
      } catch (error) {
        console.error('Error in speaker change callback:', error);
      }
    });
  }

  private notifyError(error: LiveKitError): void {
    this.errorCallbacks.forEach((callback) => {
      try {
        callback(error);
      } catch (err) {
        console.error('Error in error callback:', err);
      }
    });
  }
}

export const livekitService = new LiveKitService();

export const connect = (roomUrl: string, token: string): Promise<RoomInfo> =>
  livekitService.connect(roomUrl, token);

export const disconnect = (): void => livekitService.disconnect();

export const toggleMute = (): boolean => livekitService.toggleMute();

export const onSpeakerChange = (callback: SpeakerChangeCallback): (() => void) =>
  livekitService.onSpeakerChange(callback);

export const getScenarios = (): Promise<ScenarioInfo[]> =>
  livekitService.getScenarios();

export const createSession = (scenarioId: string, scenarioName: string, role: string): Promise<SessionResponse> =>
  livekitService.createSession(scenarioId, scenarioName, role);

export default livekitService;
